/* ============================================================
   ELLIANOS CARES — AWARENESS-RIBBON BACKGROUND
   Ambient hero background for the Breast Cancer Awareness Month LTO:
   wide pink satin ribbons with swallowtail ends that travel across the
   hero in gently waving lanes, each tying itself into one awareness-ribbon
   loop on its way through. A fine grain overlay sits on top so the flat
   pink field reads like paper rather than a gradient.

   The field is light, so type on it stays the slide scheme's plum
   (#5b2035); no inversion is needed. The flat field and the grain are CSS,
   so no-JS and reduced-motion visitors still get the correct hero colour.

   Ported from the LTO design file `ellianos-cares-ribbon-bg-v8.html`
   (October / Breast Cancer Awareness Month 2026). Differences from the
   standalone design, all forced by living inside a section instead of the
   viewport:
     · sizes to its own canvas, not the window
     · re-solves the lane layout only on a real resize, so a slide changing
       the hero height by a few pixels does not reshuffle the ribbons (and
       does not pay for the solver again)
     · re-initialises on `shopify:section:load` for the theme editor
     · pauses while its layer is inactive, off screen or the tab is hidden
   Rendered by snippets/slideshow-cares-bg.liquid.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- tuning (unchanged from the design file) ---------- */
  var SPEED = 0.11; // travel speed, as a share of the hero's long side per second
  var SWAY = 0.35; // how fast the whole field gently sways
  var WIDTH = 0.027; // ribbon width, as a share of the hero's long side
  var LENGTH = 2.0; // ribbon length, as a share of the hero's long side
  var GAP = 0.6; // minimum space between ribbons, as a share of ribbon width
  var MIN_LOOPS = 2; // loops that must be fully on screen at every moment

  var TONES = ['#efb5cf', '#f4bed6']; // alternating ribbon tones (from the LTO banner)

  /*
    How the rules are guaranteed (not just likely):
    · The hero is divided into lanes: gently waving paths spread evenly across
      it. Each lane has exactly ONE awareness-ribbon loop, fully on screen, so
      every ribbon that travels the lane loops exactly once.
    · Lanes are generated, then measured, and regenerated until every lane stays
      at least one ribbon-width plus GAP away from every other, so ribbons in
      different lanes can't touch.
    · Ribbons follow each other down a lane at one shared speed, spaced more than
      a full loop apart, so ribbons in the same lane can't touch either.
      (Each ribbon still crosses itself inside its own loop; that's the loop.)
    · Because every ribbon shares one speed, the whole picture repeats on a fixed
      cycle. Each lane's starting offset is chosen by checking that full cycle,
      with a safety margin, so the number of complete loops on screen never
      drops below MIN_LOOPS. There are always at least MIN_LOOPS + 1 lanes.
  */

  function rand(a, b) {
    return a + Math.random() * (b - a);
  }

  function smooth(pts, r) {
    // box blur along the path, three passes ≈ gaussian
    var n = pts.length,
      d = 2 * r + 1;
    if (n < d + 2) return pts;
    for (var pass = 0; pass < 3; pass++) {
      var out = pts.slice(),
        sx = 0,
        sy = 0,
        i;
      for (i = 0; i < d; i++) {
        sx += pts[i][0];
        sy += pts[i][1];
      }
      for (i = r; i < n - r; i++) {
        out[i] = [sx / d, sy / d];
        if (i + r + 1 < n) {
          sx += pts[i + r + 1][0] - pts[i - r][0];
          sy += pts[i + r + 1][1] - pts[i - r][1];
        }
      }
      pts = out;
    }
    return pts;
  }

  /* ---------- pause/resume contract, shared with the other background layers ----------
     A background may be rendered inside a layer carrying data-bg-layer="<key>" and
     data-bg-state="active|inactive" (snippets/slideshow-backgrounds.liquid). The RAF
     loop runs only when the layer is not inactive, the canvas is on screen, and the
     tab is visible. The current attribute is read at init and observed for changes,
     so this is immune to the order the deferred scripts happen to run in. With no
     [data-bg-layer] ancestor the background is simply always active. */
  function gate(canvas, start, stop) {
    var layer = canvas.closest ? canvas.closest('[data-bg-layer]') : null;
    var onScreen = true;
    var active = true;

    function sync() {
      if (active && onScreen && !document.hidden) start();
      else stop();
    }

    if (layer) {
      active = layer.getAttribute('data-bg-state') !== 'inactive';
      if (window.MutationObserver) {
        new MutationObserver(function () {
          active = layer.getAttribute('data-bg-state') !== 'inactive';
          sync();
        }).observe(layer, { attributes: true, attributeFilter: ['data-bg-state'] });
      }
    }

    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[entries.length - 1].isIntersecting;
        sync();
      }).observe(canvas);
    }

    document.addEventListener('visibilitychange', sync);
    sync();
    return sync;
  }

  /* ---------- one background instance ---------- */
  function RibbonField(canvas) {
    var ctx = canvas.getContext('2d');
    if (!ctx) return false;

    var root = canvas.parentElement;
    var grain = root ? root.querySelector('.cares-bg__grain') : null;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var W = 0,
      H = 0,
      DPR = 1,
      M = 1,
      width = 30,
      STEP = 3,
      v = 100;
    var L = 1,
      P = 1,
      pad = 1,
      R = 1,
      swayA = 0,
      swayL = 1;
    var lanes = [],
      T = 0;
    var seedW = 0,
      seedH = 0,
      sync = function () {}; // replaced by gate() below; a no-op until then

    if (grain) {
      var g = document.createElement('canvas');
      g.width = g.height = 160;
      var gx = g.getContext('2d'),
        img = gx.createImageData(160, 160);
      for (var i = 0; i < img.data.length; i += 4) {
        var c0 = Math.random() < 0.5 ? 255 : 90;
        img.data[i] = img.data[i + 1] = img.data[i + 2] = c0;
        img.data[i + 3] = Math.random() * 14;
      }
      gx.putImageData(img, 0, 0);
      grain.style.backgroundImage = 'url(' + g.toDataURL() + ')';
    }

    /* ---------- lane geometry ---------- */
    // Lanes live in a frame turned slightly off the hero's long side:
    // "u" runs along the lanes, "w" across them, both measured from the centre.
    var FR = { cx: 0, cy: 0, ux: 1, uy: 0, wx: 0, wy: 1, U: 1, Wd: 1 };
    function setFrame(tilt) {
      var th = (W >= H ? 0 : Math.PI / 2) + tilt,
        c = Math.cos(th),
        s = Math.sin(th);
      FR = {
        cx: W / 2,
        cy: H / 2,
        ux: c,
        uy: s,
        wx: -s,
        wy: c,
        U: (W * Math.abs(c) + H * Math.abs(s)) / 2,
        Wd: (W * Math.abs(s) + H * Math.abs(c)) / 2
      };
    }
    function toScreen(u, w) {
      return [FR.cx + u * FR.ux + w * FR.wx, FR.cy + u * FR.uy + w * FR.wy];
    }
    // the stretch of a straight line at offset w that is on screen, as a u-interval
    function onScreenU(w, inset) {
      var lo = -1e9,
        hi = 1e9;
      [
        [FR.ux, FR.cx + w * FR.wx, W],
        [FR.uy, FR.cy + w * FR.wy, H]
      ].forEach(function (a) {
        var d = a[0],
          p = a[1],
          mn = inset,
          mx = a[2] - inset;
        if (Math.abs(d) < 1e-6) {
          if (p < mn || p > mx) {
            lo = 1;
            hi = 0;
          }
          return;
        }
        var t0 = (mn - p) / d,
          t1 = (mx - p) / d;
        lo = Math.max(lo, Math.min(t0, t1));
        hi = Math.min(hi, Math.max(t0, t1));
      });
      return [lo, hi];
    }

    // c: how far the curl drifts forward (smaller = rounder loop); uX: where it crosses itself
    var CURL = 0.18,
      UX = (function () {
        var lo = 0.01,
          hi = Math.PI;
        for (var k = 0; k < 40; k++) {
          var m = (lo + hi) / 2;
          if (CURL * m < Math.sin(m)) lo = m;
          else hi = m;
        }
        return lo;
      })();

    function buildLane(spec) {
      var raw = [],
        marks = [],
        step = 5,
        x,
        k;
      function waveAt(u) {
        return (
          spec.base +
          spec.A1 * Math.sin(u / spec.l1 + spec.p1) +
          spec.A2 * Math.sin(u / spec.l2 + spec.p2)
        );
      }
      function slopeAt(u) {
        return (
          (spec.A1 / spec.l1) * Math.cos(u / spec.l1 + spec.p1) +
          (spec.A2 / spec.l2) * Math.cos(u / spec.l2 + spec.p2)
        );
      }
      var half = CURL * Math.PI * R,
        loops = spec.loops.slice().sort(function (a, b) {
          return a - b;
        }),
        li = 0;
      for (x = -FR.U - width * 4; x <= FR.U + width * 4; x += step) {
        if (li < loops.length && x >= loops[li] - half) {
          // splice in a curl, turned to follow the lane at that point
          var u0 = loops[li],
            w0 = waveAt(u0),
            ang = Math.atan(slopeAt(u0)),
            cs = Math.cos(ang),
            sn = Math.sin(ang);
          var n = Math.max(36, Math.round((R * 2 * Math.PI) / 4)),
            mark = { a: 0, b: 0, cu: u0, cw: w0 - spec.side * R };
          for (k = 0; k <= n; k++) {
            var t = -Math.PI + (2 * Math.PI * k) / n;
            if (!mark.ia && t >= -UX) mark.ia = raw.length;
            if (t <= UX) mark.ib = raw.length;
            var lx = R * (CURL * t - Math.sin(t)),
              ly = spec.side * R * (Math.cos(t) + 1) * -1;
            raw.push([u0 + lx * cs - ly * sn, w0 + lx * sn + ly * cs]);
          }
          marks.push(mark);
          x = u0 + half;
          li++;
          continue;
        }
        raw.push([x, waveAt(x)]);
      }
      raw = smooth(raw, Math.max(2, Math.round((M * 0.015) / step)));
      if (spec.reverse) {
        raw.reverse();
        marks.forEach(function (m) {
          var t = m.ia;
          m.ia = raw.length - 1 - m.ib;
          m.ib = raw.length - 1 - t;
        });
      }
      // even arc-length spacing; record the loops' crossing points as arc positions
      var pts = [toScreen(raw[0][0], raw[0][1])],
        carry = 0,
        run = 0;
      for (var j = 1; j < raw.length; j++) {
        var a = raw[j - 1],
          b = raw[j];
        var dx = b[0] - a[0],
          dy = b[1] - a[1],
          seg = Math.hypot(dx, dy) || 1e-6,
          pos = STEP - carry;
        marks.forEach(function (m) {
          if (j === m.ia) m.a = run;
          if (j === m.ib) m.b = run;
        });
        while (pos <= seg) {
          pts.push(toScreen(a[0] + (dx * pos) / seg, a[1] + (dy * pos) / seg));
          pos += STEP;
        }
        carry = seg - (pos - STEP);
        run += seg;
      }
      // normals, computed once (the lanes themselves never change shape)
      var nrm = pts.map(function (p, i) {
        var q0 = pts[Math.max(i - 1, 0)],
          q1 = pts[Math.min(i + 1, pts.length - 1)];
        var tx = q1[0] - q0[0],
          ty = q1[1] - q0[1],
          tl = Math.hypot(tx, ty) || 1;
        return [-ty / tl, tx / tl];
      });
      return { pts: pts, nrm: nrm, len: (pts.length - 1) * STEP, loops: marks, spec: spec };
    }

    // how many of a lane's loops are fully covered by a ribbon at a given phase.
    // `shrink` trims each covered stretch at both ends, as a safety margin.
    function covered(lane, phase, shrink) {
      var n = 0;
      for (var i = 0; i < lane.loops.length; i++) {
        var m = lane.loops[i],
          win = L - (m.b - m.a) - 2 * pad;
        var into = (((phase - m.b - pad) % P) + P) % P;
        if (into >= shrink && into <= win - shrink) n++;
      }
      return n;
    }

    // All ribbons share one speed, so the whole picture repeats every P/v seconds.
    // Pick each lane's starting offset so that, across that full cycle, the total
    // number of complete loops never drops below MIN_LOOPS (with a safety margin).
    function chooseOffsets(list) {
      var SAMPLES = 360,
        shrink = v * 0.35,
        curves = list.map(function (lane) {
          var c = new Int8Array(SAMPLES);
          for (var k = 0; k < SAMPLES; k++) c[k] = covered(lane, (P * k) / SAMPLES, shrink);
          return c;
        });
      var best = null,
        bestMin = -1,
        bestAvg = 0;
      for (var trial = 0; trial < 400; trial++) {
        var offs = list.map(function (_, i) {
          return i ? Math.floor(Math.random() * SAMPLES) : 0;
        });
        var mn = 99,
          sum = 0;
        for (var k = 0; k < SAMPLES && mn >= bestMin; k++) {
          var tot = 0;
          for (var i = 0; i < list.length; i++) tot += curves[i][(k + offs[i]) % SAMPLES];
          if (tot < mn) mn = tot;
          sum += tot;
        }
        if (mn > bestMin || (mn === bestMin && sum > bestAvg)) {
          bestMin = mn;
          bestAvg = sum;
          best = offs;
        }
        if (bestMin >= MIN_LOOPS + 1) break;
      }
      return bestMin >= MIN_LOOPS
        ? best.map(function (o) {
            return (P * o) / SAMPLES;
          })
        : null;
    }

    // Closest approach between any two points that could belong to different
    // ribbons: points on different lanes, or points on the same lane farther
    // apart along it than the gap between ribbons. Uses a coarse spatial hash.
    function lanesApart(list, need) {
      var selfArc = P - L - width;
      var cell = need,
        map = new Map();
      function key(x, y) {
        return (Math.floor(x / cell) + 1000) * 4096 + Math.floor(y / cell) + 1000;
      }
      for (var li = 0; li < list.length; li++) {
        var pts = list[li].pts;
        for (var i = 0; i < pts.length; i += 2) {
          var p = pts[i];
          if (p[0] < -need || p[1] < -need || p[0] > W + need || p[1] > H + need) continue;
          var gx = Math.floor(p[0] / cell),
            gy = Math.floor(p[1] / cell);
          for (var ox = -1; ox <= 1; ox++)
            for (var oy = -1; oy <= 1; oy++) {
              var bucket = map.get((gx + ox + 1000) * 4096 + gy + oy + 1000);
              if (!bucket) continue;
              for (var b = 0; b < bucket.length; b++) {
                var q = bucket[b];
                if (
                  (q[2] !== li || i * STEP - q[3] > selfArc) &&
                  (q[0] - p[0]) * (q[0] - p[0]) + (q[1] - p[1]) * (q[1] - p[1]) < need * need
                )
                  return false;
              }
            }
          var k = key(p[0], p[1]);
          if (!map.has(k)) map.set(k, []);
          map.get(k).push([p[0], p[1], li, i * STEP]);
        }
      }
      return true;
    }

    function layout() {
      var need = width * (1 + GAP) + swayA * 2 + 2; // centre-to-centre clearance
      var foot = 2 * R + need; // across-lane room one lane + its loops needs
      var fit = R * 1.08 + width * 0.6 + 4; // loop centre to edge: whole loop shows

      // try many layouts and keep the one with the most lanes (fuller, more even hero)
      var keep = null;
      for (var attempt = 0; attempt < 70; attempt++) {
        if (keep && (keep.list.length >= 4 || attempt >= 35)) break;
        var late = attempt / 70;
        setFrame((Math.random() < 0.5 ? -1 : 1) * rand(0.12, 0.36) * (1 - late));

        // stack lanes across the frame; every loop curls toward +w, so lane i's
        // loops need foot of room before lane i+1. Keep lanes with enough room for a loop.
        var slackPer = rand(0.12, 0.4) * foot,
          pitch = foot + slackPer;
        var count = Math.floor((2 * FR.Wd - 2 * R - 2 * width) / pitch) + 1;
        var start = -((count - 1) * pitch + 2 * R) / 2 + rand(-0.15, 0.15) * slackPer,
          usable = [];
        for (var c = 0; c < count; c++) {
          var wpos = start + c * pitch,
            iv = onScreenU(wpos + R, fit);
          if (iv[1] - iv[0] > R * 2.5) usable.push({ w: wpos, iv: iv });
        }
        if (usable.length < MIN_LOOPS + 1) continue; // need a spare lane to hand loops off

        var calm = 1 - late * 0.8;
        var flow = { A: foot * rand(0.3, 0.7) * calm, l: M * rand(0.14, 0.22), p: rand(0, 6.283) };
        var list = [],
          ok = true;
        for (var i = 0; i < usable.length && ok; i++) {
          var ln = usable[i],
            room = ln.iv[1] - ln.iv[0];
          var spec = {
            base: ln.w,
            A1: flow.A,
            l1: flow.l,
            p1: flow.p,
            A2: slackPer * rand(0.1, 0.35) * calm,
            l2: M * rand(0.06, 0.09),
            p2: rand(0, 6.283),
            side: -1,
            reverse: Math.random() < 0.5,
            loops: []
          };
          var lane = null;
          for (var tries = 0; tries < 14 && !lane; tries++) {
            spec.loops = [];
            // neighbouring lanes' loops are staggered along the lane
            var f = i % 2 ? rand(0.55, 0.85) : rand(0.15, 0.45);
            spec.loops.push(ln.iv[0] + room * f); // one loop per lane = one loop per ribbon
            var cand = buildLane(spec),
              inside = true;
            cand.loops.forEach(function (lp) {
              var pc = toScreen(lp.cu, lp.cw);
              if (pc[0] < fit || pc[1] < fit || pc[0] > W - fit || pc[1] > H - fit) inside = false;
            });
            if (inside) lane = cand;
          }
          if (!lane) ok = false;
          else list.push(lane);
        }
        if (!ok || !lanesApart(list, need)) continue;
        var offs = chooseOffsets(list);
        if (offs && (!keep || list.length > keep.list.length))
          keep = { list: list, offs: offs, frame: FR };
      }
      if (keep) {
        FR = keep.frame;
        finish(keep.list, keep.offs);
        return true;
      }
      return false;
    }

    // last resort: straight, evenly stacked lanes, one loop each
    function layoutStraight() {
      var need = width * (1 + GAP) + swayA * 2 + 2,
        fit = R * 1.08 + width * 0.6 + 4;
      setFrame(0);
      var n = MIN_LOOPS + 1,
        pitch = (2 * FR.Wd - 2 * fit) / n,
        list = [];
      for (var attempt = 0; attempt < 40; attempt++) {
        list = [];
        for (var i = 0; i < n; i++) {
          var base = -FR.Wd + fit + i * pitch + (pitch - 2 * R) / 2;
          var spec = {
            base: base,
            A1: 0,
            l1: 1,
            p1: 0,
            A2: 0,
            l2: 1,
            p2: 0,
            side: -1,
            reverse: i % 2 === 1,
            loops: []
          };
          var iv = onScreenU(base + R, fit);
          spec.loops.push(iv[0] + (iv[1] - iv[0]) * (i % 2 ? rand(0.55, 0.85) : rand(0.15, 0.45)));
          list.push(buildLane(spec));
        }
        var offs = lanesApart(list, need) && chooseOffsets(list);
        if (offs) {
          finish(list, offs);
          return;
        }
      }
      finish(
        list,
        list.map(function (_, i) {
          return (P * i) / list.length;
        })
      );
    }

    function finish(list, offs) {
      var common = rand(0, P);
      lanes = list.map(function (lane, i) {
        lane.phase = common + offs[i];
        lane.tone = i % 2;
        return lane;
      });
    }

    /* ---------- sizing ----------
       measure() is cheap and runs on every box change so the bitmap always
       matches the element. build() runs the lane solver and is expensive, so
       it only runs when the hero has really changed size — see onResize. */
    function measure() {
      var r = canvas.getBoundingClientRect();
      W = Math.max(1, r.width);
      H = Math.max(1, r.height);
      DPR = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * DPR);
      canvas.height = Math.round(H * DPR);
    }

    function build() {
      M = Math.max(W, H);
      width = Math.min(58, Math.max(22, M * WIDTH));
      v = M * SPEED;
      L = M * LENGTH;
      pad = width * 0.9;
      swayA = M * 0.006;
      swayL = M * 0.3;
      seedW = W;
      seedH = H;
      // try full-size loops first; on cramped heroes, shrink them until enough lanes fit
      var sizes = [1, 0.88, 0.76, 0.66];
      for (var si = 0; si < sizes.length; si++) {
        R = Math.max(width * 1.75, Math.min(W, H) * 0.07, M * 0.04) * sizes[si];
        // ribbons in a lane are spaced by more than one full loop, so they never meet at a crossing
        P = L + 2 * Math.PI * R * 1.05 + width * 2.5;
        if (layout()) return;
      }
      layoutStraight();
    }

    /* ---------- drawing ---------- */
    // a slow, smooth sway applied to the whole field; nearby points move together,
    // so the spacing between ribbons is preserved
    var swX = 0,
      swY = 0;
    function sway(x, y) {
      return [x + swayA * Math.sin(y / swayL + swX), y + swayA * Math.sin((x / swayL) * 1.3 + swY)];
    }

    function at(lane, s) {
      var N = lane.pts.length,
        f = Math.min(Math.max(s / STEP, 0), N - 1.001),
        j = Math.floor(f),
        t = f - j;
      var p0 = lane.pts[j],
        p1 = lane.pts[j + 1],
        n0 = lane.nrm[j],
        n1 = lane.nrm[j + 1];
      var nx = n0[0] + (n1[0] - n0[0]) * t,
        ny = n0[1] + (n1[1] - n0[1]) * t,
        nl = Math.hypot(nx, ny) || 1;
      return {
        x: p0[0] + (p1[0] - p0[0]) * t,
        y: p0[1] + (p1[1] - p0[1]) * t,
        nx: nx / nl,
        ny: ny / nl
      };
    }

    function drawSegment(lane, s0, s1) {
      var len = lane.len,
        half = width / 2,
        notch = width * 0.8;
      var a = Math.max(0, s0 + notch - 1),
        b = Math.min(len, s1 - notch + 1),
        p,
        q;
      if (b > a) {
        p = at(lane, a);
        q = sway(p.x, p.y);
        ctx.beginPath();
        ctx.moveTo(q[0], q[1]);
        for (var k = Math.ceil(a / STEP); k * STEP < b; k++) {
          q = sway(lane.pts[k][0], lane.pts[k][1]);
          ctx.lineTo(q[0], q[1]);
        }
        p = at(lane, b);
        q = sway(p.x, p.y);
        ctx.lineTo(q[0], q[1]);
        ctx.stroke();
      }
      function tail(tipS, valleyS) {
        // two-point swallowtail
        var tp = at(lane, tipS),
          vp = at(lane, valleyS);
        var pts = [
          sway(vp.x + vp.nx * half, vp.y + vp.ny * half),
          sway(tp.x + tp.nx * half, tp.y + tp.ny * half),
          sway(vp.x, vp.y),
          sway(tp.x - tp.nx * half, tp.y - tp.ny * half),
          sway(vp.x - vp.nx * half, vp.y - vp.ny * half)
        ];
        ctx.beginPath();
        ctx.moveTo(pts[0][0], pts[0][1]);
        for (var i = 1; i < 5; i++) ctx.lineTo(pts[i][0], pts[i][1]);
        ctx.closePath();
        ctx.fill();
      }
      if (s0 >= 0 && s0 + notch < len) tail(s0, s0 + notch);
      if (s1 <= len && s1 - notch > 0) tail(s1, s1 - notch);
    }

    // every ribbon currently on a lane: heads sit one period P apart
    function ribbonsOn(lane, fn) {
      var first = lane.phase % P;
      for (var h = first, k = 0; h - L < lane.len; h += P, k++) if (h > 0) fn(h - L, h, k);
    }

    function render() {
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.lineWidth = width;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'butt';
      swX = T * SWAY * 0.9;
      swY = T * SWAY * 0.7 + 1.7;
      for (var tone = 0; tone < 2; tone++) {
        ctx.fillStyle = ctx.strokeStyle = TONES[tone];
        lanes.forEach(function (lane) {
          ribbonsOn(lane, function (s0, s1, k) {
            if (((k + lane.tone) & 1) === tone) drawSegment(lane, s0, s1);
          });
        });
      }
    }

    function update(dt) {
      lanes.forEach(function (lane) {
        lane.phase = (lane.phase + v * dt) % (P * 1000);
      });
    }

    var last = 0,
      raf = 0;
    function frame(now) {
      var dt = last ? (now - last) / 1000 : 0;
      if (dt > 0.25) dt = 1 / 60;
      last = now;
      T += dt;
      update(dt);
      render();
      raf = requestAnimationFrame(frame);
    }
    function start() {
      if (raf) return;
      last = 0;
      if (reduce.matches) {
        render();
        return;
      }
      raf = requestAnimationFrame(frame);
    }
    function stop() {
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
    }

    /* A slide can change the hero's height by a few pixels as the slideshow
       advances. Re-solving the lanes for that would be both expensive and
       visible, so only a real size change rebuilds; anything smaller just
       resizes the bitmap. */
    function onResize() {
      var r = canvas.getBoundingClientRect();
      var nw = Math.max(1, r.width),
        nh = Math.max(1, r.height);
      var real =
        Math.abs(nw - seedW) > Math.max(24, seedW * 0.12) ||
        Math.abs(nh - seedH) > Math.max(24, seedH * 0.12);
      stop();
      measure();
      if (real) build();
      render();
      sync();
    }

    measure();
    build();
    render();

    var rt = 0;
    function onResizeDebounced() {
      clearTimeout(rt);
      rt = setTimeout(onResize, 120);
    }
    if ('ResizeObserver' in window) new ResizeObserver(onResizeDebounced).observe(canvas);
    else window.addEventListener('resize', onResizeDebounced, { passive: true });
    if (reduce.addEventListener)
      reduce.addEventListener('change', function () {
        stop();
        sync();
      });

    // The gate handles off-screen, hidden-tab and inactive-layer pausing, and
    // starts the loop when all three allow it.
    sync = gate(canvas, start, stop);
    return true;
  }

  /* ---------- bootstrap: idempotent, and re-runs for the theme editor ---------- */
  function init() {
    var canvases = document.querySelectorAll('.cares-bg__canvas');
    for (var i = 0; i < canvases.length; i++) {
      var cv = canvases[i];
      if (cv.dataset.caresBgReady === 'true') continue;
      cv.dataset.caresBgReady = 'true';
      RibbonField(cv);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
